import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { incidentManagementAPI } from '@/shared/lib/api';
import type {
  Incident,
  IncidentCategory,
  EmployeeCreditBalance,
  DegreeCalculation,
  IncidentStatistics,
  CreateIncidentFormData,
  ApproveIncidentFormData,
  UpdateIncidentFormData,
  PaginatedResponse,
} from '../types';

// ==========================================
// INCIDENTS
// ==========================================

export function useIncidents(params?: {
  page?: number;
  limit?: number;
  employeeId?: string;
  categoryId?: string;
  status?: string;
  appliedDegree?: string;
  startDate?: string;
  endDate?: string;
}) {
  return useQuery<PaginatedResponse<Incident>>({
    queryKey: ['incident-management', 'incidents', params],
    queryFn: async () => {
      const res = await incidentManagementAPI.listIncidents(params);
      // Backend returns { data: incidents[], pagination: {...} }
      return {
        data: res.data.data || [],
        pagination: res.data.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 }
      };
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useIncident(id: string | undefined) {
  return useQuery<Incident>({
    queryKey: ['incident-management', 'incidents', id],
    queryFn: async () => {
      if (!id) throw new Error('Incident ID is required');
      const res = await incidentManagementAPI.getIncidentById(id);
      return res.data.data;
    },
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
}

export function useEmployeeHistory(employeeId: string | undefined) {
  return useQuery<Incident[]>({
    queryKey: ['incident-management', 'employee-history', employeeId],
    queryFn: async () => {
      if (!employeeId) throw new Error('Employee ID is required');
      const res = await incidentManagementAPI.getEmployeeHistory(employeeId);
      return res.data.data ?? [];
    },
    enabled: !!employeeId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCalculateDegree(employeeId: string | undefined) {
  return useQuery<DegreeCalculation>({
    queryKey: ['incident-management', 'calculate-degree', employeeId],
    queryFn: async () => {
      if (!employeeId) throw new Error('Employee ID is required');
      const res = await incidentManagementAPI.calculateDegree(employeeId);
      return res.data.data;
    },
    enabled: !!employeeId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

export function useIncidentStatistics(params?: {
  startDate?: string;
  endDate?: string;
  employeeId?: string;
  categoryId?: string;
}) {
  return useQuery<IncidentStatistics>({
    queryKey: ['incident-management', 'statistics', params],
    queryFn: async () => {
      const res = await incidentManagementAPI.getStatistics(params);
      return res.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateIncident() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateIncidentFormData) => {
      const res = await incidentManagementAPI.createIncident(data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'incidents'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'statistics'] });
    },
  });
}

export function useUpdateIncident() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateIncidentFormData }) => {
      const res = await incidentManagementAPI.updateIncident(id, data);
      return res.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'incidents'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'incidents', variables.id] });
    },
  });
}

export function useApproveIncident() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ApproveIncidentFormData }) => {
      const res = await incidentManagementAPI.approveIncident(id, data);
      return res.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'incidents'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'incidents', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'employee-history'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'statistics'] });
    },
  });
}

export function useCancelIncident() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const res = await incidentManagementAPI.cancelIncident(id, reason);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'incidents'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'statistics'] });
    },
  });
}

// ==========================================
// CREDIT BALANCE
// ==========================================

export function useCreditBalance(employeeId: string | undefined) {
  return useQuery<EmployeeCreditBalance>({
    queryKey: ['incident-management', 'credit-balance', employeeId],
    queryFn: async () => {
      if (!employeeId) throw new Error('Employee ID is required');
      const res = await incidentManagementAPI.getCreditBalance(employeeId);
      return res.data.data;
    },
    enabled: !!employeeId,
    staleTime: 5 * 60 * 1000,
  });
}

// ==========================================
// CATEGORIES
// ==========================================

export function useCategories() {
  return useQuery<IncidentCategory[]>({
    queryKey: ['incident-management', 'categories'],
    queryFn: async () => {
      const res = await incidentManagementAPI.listCategories();
      return res.data.data ?? [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useCategory(id: string | undefined) {
  return useQuery<IncidentCategory>({
    queryKey: ['incident-management', 'categories', id],
    queryFn: async () => {
      if (!id) throw new Error('Category ID is required');
      const res = await incidentManagementAPI.getCategoryById(id);
      return res.data.data;
    },
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      const res = await incidentManagementAPI.createCategory(data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'categories'] });
    },
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { name?: string; description?: string } }) => {
      const res = await incidentManagementAPI.updateCategory(id, data);
      return res.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'categories'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'categories', variables.id] });
    },
  });
}

export function useDeactivateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await incidentManagementAPI.deactivateCategory(id);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'categories'] });
    },
  });
}
